<script setup>
import { get_trait, get_package } from '@/services/option';
import { get_assessments, create_assessment } from '@/services/assessments';
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { get_terms, create_term_relationship } from '@/services/terms';
import { sortAgeGroup, swalError } from '@/helper';

const route = useRoute()
const isLoading = ref(true)
const package_config = ref({})
const trait = ref({})
const assessment = ref({
  name: '',
  code: '',
  indicators_attributes: [],
  trait_id: ''
})
const assessment_term = ref()
const ageGroupOption = ref([])

onMounted(async ()=>{
  assessment.value.trait_id = route.params.trait_id
  let response = await get_package(route.params.package_id)
  if (response.error) {
    if (!response.isRedirecting) swalError(response.message || 'Failed to load package')
    return
  }
  package_config.value = response.result
  
  let trait_response = await get_trait(route.params.trait_id)
  if (trait_response.error) {
    if (!trait_response.isRedirecting) swalError(trait_response.message || 'Failed to load trait')
    return
  }
  trait.value = trait_response.result
  
  let age_group_response = await get_terms('age_group')
  if (age_group_response.error) {
    if (!age_group_response.isRedirecting) swalError(age_group_response.message || 'Failed to load age groups')
    return
  }
  ageGroupOption.value = generateOption(age_group_response.result.data.data)
  setAgeGroupValue()
  isLoading.value = false
})

function setAgeGroupValue(){
  let temp_age_group = route.query.age_group
  if (temp_age_group){
    let isFound = ageGroupOption.value.find(item => item.label_id == temp_age_group)
    if (isFound){
      assessment_term.value = isFound.valueProp
    }
  }
}

function generateOption(reponse){
  reponse = reponse.map(item => {
    return {
      valueProp: item.relationships.term_taxonomy.data.id,
      label: item.attributes.name,
      label_id: item.id,
      disabled: false
    }
  })
  return sortAgeGroup(reponse)
}

const temp_indicator = ref('')
function addIndicator(){
  assessment.value.indicators_attributes.push({
    name: temp_indicator.value,
    id: '',
    _destroy: false
  })
  temp_indicator.value = ''
}

function removeIndicator(index){
  assessment.value.indicators_attributes.splice(index, 1)
}

const isSubmitting = ref(false)
async function createAssessment(){
  let isPass = checkForm()
  if (isPass.error){
    swalError(isPass.msg)
    return
  }
  const assessmentParams = {
    assessment_obj:{
      ...assessment.value
    },
    term_relationship:{
      term_taxonomy_id: assessment_term.value
    }
  }
  isSubmitting.value = true
  let response = await create_assessment(assessmentParams)
  isSubmitting.value = false
  if (response.error){
    if (!response.isRedirecting) {
      Swal.fire({
        title: 'Error',
        html: response.message || 'Failed to create assessment',
        icon: 'error'
      })
    }
  }else{
    Swal.fire({
      title: 'Created',
      text: "Assessment has been created",
      icon: 'success'
    })
  }
}

function checkForm(){
  let isPass = {error: false, msg: ''}
  if (assessment.value.name == '' || assessment.value.code == ''){
    isPass.msg = 'Form is not completed'
    isPass.error = true
  }else if (assessment.value.indicators_attributes.length == 0){
    isPass.msg = 'Indicator is empty'
    isPass.error = true
  }else if (temp_indicator.value != ''){
    isPass.msg = 'Please check the indicator form'
    isPass.error = true
  }
  return isPass
}
</script>

<template>
  <div class="container-fluid">
    <Breadcrumbs 
      main="Assessment and Indicators" 
      :title="trait.data?.attributes.name + ' - New Assessment'"
      :mainRoute="{name: 'assessment-list', params: {package_id: route.params.package_id, trait_id: route.params.trait_id}}"
    />

    <LoadingCard v-if="isLoading" />
    <template v-else>
      <!-- Page title -->
      <div class="d-flex align-items-center">
        <div>
          <h4 class="mb-0">Add assessment for {{ trait.data.attributes.name }}</h4>
        </div>
      </div>

      <div class="row mt-3">
        <div class="col-9 card">
          <div class="card-body">
            <div class="form-items mt-3">
              <div class="row">
                <div class="col-3">
                  <label class="form-label" for="code">Code</label>
                  <input v-model="assessment.code" class="form-control" id="code" type="text" placeholder="Code" required>
                </div>
                <div class="col-12">
                  <label class="form-label" for="assessment_description">Assessment Description</label>
                  <textarea rows="4" v-model="assessment.name"
                    id="assessment_description" placeholder="Assessment Description" 
                    class="form-control"
                    required
                  ></textarea>
                </div>
              </div>
  
              <div v-auto-animate class="mt-4">
                <div>
                  <h6 class="mb-0">Indicators</h6>
                  <p class="text-secondary">Add indicators for assessment</p>
                </div>
                <form @submit.prevent="addIndicator()" class="row mt-3">
                  <div class="col-6">
                    <input class="form-control" 
                      type="text" 
                      placeholder="Indicator Description" required
                      v-model="temp_indicator"
                    >
                  </div>
                  <div class="col-2">
                    <button type="submit" class="btn-rounded-icon p-0 btn btn-success"
                    >
                      <vue-feather type="plus"></vue-feather>
                    </button>
                  </div>
                </form>
                <!-- items -->
                <div v-for="(indicator, index) in assessment.indicators_attributes" class="row mt-2">
                  <div class="col-6">
                    <input class="form-control" 
                      type="text" 
                      placeholder="Indicator Description" required
                      :value="indicator.name"
                    >
                  </div>
                  <div class="col-2">
                    <button class="btn-rounded-icon p-0 btn btn-danger"
                      @click="removeIndicator(index)"
                    >
                      <vue-feather type="x"></vue-feather>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Submit Section -->
        <div class="col-3">
          <div class="card">
            <div class="card-body">
              <div class="row mb-3">
                <label class="form-label" for="age_group_from">Age Group</label>
                <multiselect 
                  v-model="assessment_term" 
                  placeholder="Select age group"
                  :options="ageGroupOption"
                  valueProp="valueProp"
                  searchable="true"
                ></multiselect>
              </div>
              <button class="btn btn-light-success"
                @click="createAssessment()"
                :disabled="isSubmitting"
              >
                <div v-if="isSubmitting" class="spinner-border spinner-border-sm" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <template v-else>
                  Create Assessment
                </template>
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>